import * as React from 'react';

import {
  Button,
  Col,
  ContentSection,
  Grid,
  Money,
  Row,
} from '@rematter/ui-web';
import { useToast } from '@rematter/ui-web/hooks';
import { useAtomValue, useSetAtom } from 'jotai';
import { LucidePlusCircle, LucideTrash2 } from 'lucide-react';

import { $remoteInvoiceDetail } from '../../_state/invoiceDetail.state';
import { $removePaymentFromInvoice } from '../../_state/paymentDetail.state';
import { useTranslations } from '../../i18n/useTranslations';
import { InvoiceLinkedPaymentRecord, InvoiceRecord } from '../../types';
import { FinanceDetailLayout } from '../_layouts/FinanceDetailLayout';

import { InvoicePaymentDrawer } from './InvoicePaymentDrawer';

type Props = {
  disabled?: boolean;
};

//
// ⚛️ ⚛️ ⚛️ INVOICE TAB PAYMENTS ⚛️ ⚛️ ⚛️
//
export const InvoiceTabPayments: React.FC<Props> = ({ disabled }) => {
  const t = useTranslations();

  // States
  const [showPaymentDrawer, setShowPaymentDrawer] = React.useState(false);

  // Atoms
  const queryResult = useAtomValue($remoteInvoiceDetail);
  const removeFromInvoice = useSetAtom($removePaymentFromInvoice);

  // Other data hooks
  const toast = useToast();

  const invoice = (
    queryResult?.data as { invoice: InvoiceRecord | null } | undefined
  )?.invoice;
  const refetch = queryResult?.refetch;
  const payments = invoice?.payments ?? [];

  // Callbacks
  const handleRemove = React.useCallback(
    async (paymentId: string) => {
      if (!invoice?.id) return;
      try {
        await removeFromInvoice({ paymentId, invoiceId: invoice.id });
        toast.success(t.invoices.detail.messages.paymentRemoved);
        refetch?.();
      } catch (err) {
        toast.error(
          t.invoices.detail.messages.removePaymentError(
            err instanceof Error ? err.message : t.messages.common.unknownError,
          ),
        );
      }
    },
    [invoice?.id, removeFromInvoice, toast, refetch, t],
  );

  return (
    <FinanceDetailLayout className="@InvoiceTabPayments h-auto">
      <ContentSection
        title={
          <Row xBetween yCenter>
            <h3 className="text-lg font-semibold text-neutral/90">
              {t.invoices.detail.tabs.payments}
            </h3>
            {!disabled && (
              <Button
                variant="outline"
                icon={<LucidePlusCircle className="size-4" />}
                onPress={() => setShowPaymentDrawer(true)}
              >
                {t.invoices.detail.payments.applyPayment}
              </Button>
            )}
          </Row>
        }
      >
        <Col gap4>
          {payments.length === 0 ? (
            <div className="py-8 text-center text-neutral/60">
              {t.invoices.detail.payments.empty}
            </div>
          ) : (
            <Grid className="grid-cols-[2fr_1fr_1fr_auto]">
              <div className="col-span-full grid grid-cols-subgrid border-b border-neutral/10 pb-2 text-sm font-medium text-neutral/60">
                <div>{t.invoices.detail.payments.columns.paymentNumber}</div>
                <div>{t.invoices.detail.payments.columns.appliedAmount}</div>
                <div>{t.invoices.detail.payments.columns.date}</div>
                <div />
              </div>
              {payments.map((payment: InvoiceLinkedPaymentRecord) => (
                <div
                  key={payment.id}
                  className="col-span-full grid grid-cols-subgrid items-center border-b border-neutral/5 py-3"
                >
                  <div className="font-medium">{payment.documentNumber}</div>
                  <div>
                    <Money
                      amount={payment.appliedAmount.amount}
                      currencyCode={payment.appliedAmount.currencyCode}
                    />
                  </div>
                  <div>{payment.transactionDate?.toString() ?? '-'}</div>
                  <div>
                    <Button
                      variant="ghost"
                      color="failure"
                      icon={<LucideTrash2 className="size-4" />}
                      onPress={() => handleRemove(payment.id)}
                      isDisabled={disabled}
                      aria-label="Remove payment"
                    />
                  </div>
                </div>
              ))}
            </Grid>
          )}
        </Col>

        {invoice && (
          <InvoicePaymentDrawer
            isOpen={showPaymentDrawer}
            onClose={() => setShowPaymentDrawer(false)}
            invoices={[invoice]}
            onSuccess={() => refetch?.()}
          />
        )}
      </ContentSection>
    </FinanceDetailLayout>
  );
};
